import React from "react"
import { usePage } from "@inertiajs/react"

import { Toaster } from "@/components/ui/sonner"
import { SidebarProvider } from "@/components/ui/sidebar"
import { StaffMobileBar, StaffSidebar } from "@/components/layouts/staff-sidebar-nav"
import { StaffRealtimeProvider } from "@/components/layouts/staff-realtime"

type StaffLayoutProps = {
  children: React.ReactNode
}

/**
 * There is no navbar. Every destination, the branch switcher, the brand, the
 * notification feed and the account menu live in the sidebar, which owns the full
 * viewport height; the page starts at the top edge of the window.
 *
 * `sidebarOpen` is the `sidebar_state` cookie, read server-side and shared by
 * Staff::BaseController. The primitive writes that cookie but never reads it, so
 * without the shared prop a collapsed rail would spring back open on every visit.
 */
export function StaffLayout({ children }: StaffLayoutProps) {
  const { sidebarOpen = true } = usePage<{ sidebarOpen?: boolean }>().props

  return (
    // h-svh + overflow-hidden keeps the page pane its own scroll container, which
    // is what the old flex/h-dvh shell gave every screen: `sticky` headers inside
    // a page stick to the pane, not to a document that never scrolls.
    // surface-staff also rides on <body> (ApplicationHelper#surface_class), which is
    // what Radix portals inherit; it is repeated here so Storybook, which renders
    // without the Rails body, shows staff on its own page color.
    <SidebarProvider
      defaultOpen={sidebarOpen}
      className="surface-staff h-svh overflow-hidden print:h-auto print:overflow-visible print:[&_[data-slot=sidebar]]:hidden"
    >
      {/* Owns both realtime wires, and sits outside the sidebar because the
          mobile bar reads the feed from the page pane. The layout used to hold
          the cable itself, on a route list that disagreed with the feed's. */}
      <StaffRealtimeProvider>
        <StaffSidebar />
        {/* Not SidebarInset: that primitive is a <main>, and MainContainer — which
            every product page wraps its content in — is a <main> too. Two main
            landmarks on one page is invalid, and a screen reader announces both. */}
        <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col bg-background">
          <StaffMobileBar />
          <div className="min-h-0 flex-1 overflow-y-auto pt-4 pb-6 print:h-auto print:overflow-visible print:pt-0">
            {children}
          </div>
        </div>
      </StaffRealtimeProvider>
      <Toaster position="top-right" richColors className="print:hidden" />
    </SidebarProvider>
  )
}
